/*
 * Napp Motorsports fitment core: VIN crank check + "does this part fit my Miata" verdict.
 * Pure functions, no DOM. Loaded by the theme (window.NappFitment) and by tests (require).
 *
 * Fitment rules come from Stefan-approved product metafields (see derive.py):
 *   { years: [from, to] | null, trans: "any"|"manual"|"AT"|"5MT"|"6MT", crank: "short"|"long"|null,
 *     by_option: [{ option, value, years, trans: [..] | null, crank }] }
 * A vehicle is { year, trans: "5MT"|"6MT"|"AT", crank: "short"|"long"|"any"|null }.
 */
(function (root) {
  "use strict";

  // 1991 1.6L: VIN serial <= 209446 short nose crank, >= 209447 long nose (Stefan 2026-10-13;
  // same cutoff at flyinmiata.com and the MX5OC VIN guide). Kept in step with derive.py CRANK_SPLIT_SERIAL.
  var CRANK_SPLIT_SERIAL = 209447;
  // Every 1991 US Miata VIN decodes (NHTSA vPIC, 2026-10-13) as JM1NA351 + check digit + M (1991) + 0 (Hiroshima).
  var PREFIX_1991 = "JM1NA351";
  var VIN_VALUES = { A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8, J: 1, K: 2, L: 3, M: 4, N: 5,
    P: 7, R: 9, S: 2, T: 3, U: 4, V: 5, W: 6, X: 7, Y: 8, Z: 9 };
  var VIN_WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];
  var CRANK_YEAR = 1991;
  var LATEST = 2026;  // newest model year in the selector; ranges are closed, later years need approval
  // US transmissions by model year: fueleconomy.gov vehicle menus, fetched 2026-10-13 (no 1998 US model year).
  var GENERATIONS = [
    { code: "NA", from: 1990, to: 1997, trans: ["5MT", "AT"] },
    { code: "NB", from: 1999, to: 2005, trans: ["5MT", "6MT", "AT"] },
    { code: "NC", from: 2006, to: 2015, trans: ["5MT", "6MT", "AT"] },
    { code: "ND", from: 2016, to: LATEST, trans: ["6MT", "AT"] }
  ];
  var TRANS_LABEL = { "5MT": "5-speed manual", "6MT": "6-speed manual", AT: "Automatic" };

  function generation(year) {
    year = +year;
    for (var i = 0; i < GENERATIONS.length; i++)
      if (year >= GENERATIONS[i].from && year <= GENERATIONS[i].to) return GENERATIONS[i];
    return null;
  }
  function modelYears() {
    var out = [];
    for (var y = LATEST; y >= 1990; y--) if (generation(y)) out.push(y);
    return out;
  }
  function transFor(year) { var g = generation(year); return g ? g.trans.slice() : []; }

  function checkDigit(vin) {  // 49 CFR 565.15
    var sum = 0;
    for (var i = 0; i < 17; i++) {
      var c = vin[i];
      sum += (/\d/.test(c) ? +c : VIN_VALUES[c]) * VIN_WEIGHTS[i];
    }
    var r = sum % 11;
    return r === 10 ? "X" : String(r);
  }

  /** {ok, crank, message} for a typed VIN. Only meant for 1991 cars. */
  function crankFromVin(input) {
    var vin = String(input || "").toUpperCase().replace(/[\s-]/g, "");
    if (vin.length !== 17) return { ok: false, message: "A VIN is 17 characters. Check it and try again." };
    if (/[IOQ]/.test(vin) || !/^[A-Z0-9]{17}$/.test(vin))
      return { ok: false, message: "That doesn't look like a VIN (letters I, O and Q are never used)." };
    if (vin.slice(0, 5) !== "JM1NA") return { ok: false, message: "That isn't a 1990-1997 Miata VIN (it should start JM1NA)." };
    if (vin[9] !== "M") return { ok: false, message: "That VIN isn't a 1991 model year, so there's no crank question." };
    if (vin.slice(0, 8) !== PREFIX_1991 || vin[10] !== "0" || checkDigit(vin) !== vin[8])
      return { ok: false, message: "That VIN doesn't check out. It may be mistyped; check it against your title or windshield." };
    var serial = +vin.slice(11);
    var crank = serial >= CRANK_SPLIT_SERIAL ? "long" : "short";
    return { ok: true, crank: crank, message: crank === "long" ? "Long nose crank (VIN " + vin.slice(11) + ")."
      : "Short nose crank (VIN " + vin.slice(11) + ")." };
  }

  function needsCrankQuestion(year) { return +year === CRANK_YEAR; }

  function transFits(rule, trans) {
    if (!rule || rule === "any") return true;
    if (Array.isArray(rule)) return rule.some(function (t) { return transFits(t, trans); });
    if (rule === "manual") return trans === "5MT" || trans === "6MT";
    return rule === trans;
  }

  // "yes" | "no" | "crank" (only the crank question is open)
  function match(years, trans, crank, v) {
    if (years && (v.year < years[0] || v.year > years[1])) return "no";
    if (trans && !transFits(trans, v.trans)) return "no";
    if (crank && +v.year === CRANK_YEAR) {
      if (v.crank === "short" || v.crank === "long") return v.crank === crank ? "yes" : "no";
      return "crank";
    }
    return "yes";
  }

  /**
   * Verdict for one product: {state, choose?}
   *   ask   - no approved fitment ("Not sure? Ask us")
   *   fits  - fits as listed
   *   choose- fits with these option values ({optionName: [values]})
   *   crank - 1991 and the crank isn't known: offer the VIN check / buttons
   *   nofit - doesn't fit this vehicle
   */
  function verdict(rule, v) {
    if (!rule || !rule.years || !v || !v.year) return { state: "ask" };
    var base = match(rule.years, rule.trans, rule.crank, v);
    if (base === "no") return { state: "nofit" };
    var opts = rule.by_option || [];
    if (!opts.length) return { state: base === "crank" ? "crank" : "fits" };
    var byName = {};
    opts.forEach(function (o) { (byName[o.option] = byName[o.option] || []).push(o); });
    var choose = {}, open = base === "crank";
    for (var name in byName) {
      var ok = [], pending = false;
      byName[name].forEach(function (o) {
        var m = match(o.years, o.trans, o.crank, v);
        if (m === "yes") ok.push(o.value);
        if (m === "crank") { pending = true; ok.push(o.value); }
      });
      if (!ok.length) return { state: "nofit" };
      if (pending) open = true;
      choose[name] = ok;
    }
    return { state: open ? "crank" : "choose", choose: choose };
  }

  var api = { crankFromVin: crankFromVin, checkDigit: checkDigit, needsCrankQuestion: needsCrankQuestion,
    verdict: verdict, generation: generation, modelYears: modelYears, transFor: transFor, TRANS_LABEL: TRANS_LABEL,
    CRANK_SPLIT_SERIAL: CRANK_SPLIT_SERIAL };
  if (typeof module === "object" && module.exports) module.exports = api;
  else root.NappFitment = api;
})(this);
